// ABOUTME: Smart wrapper for the Pump Settings entity — owns usePumpSettingsEntity and reports state via context.
// ABOUTME: Renders skeleton/error/form; computes UoM and reference event options from catalog.
import React, {useMemo} from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import {usePumpSettingsEntity} from '../hooks/usePumpSettingsEntity'
import {useEntityFormContext, useRegisterEntityForm} from '../context/EntityFormContext'
import {EntityContentEmpty} from './EntityContentEmpty'
import {EntityContentError} from './EntityContentError'
import {EntityContentSkeleton} from './EntityContentSkeleton'
import {PumpSettingsForm} from './PumpSettingsForm'

const UOM_ORDER = ['Business Minute', 'Business Hour', 'Business Day', 'Minute', 'Hour', 'Day']

export const PumpSettingsEntityForm: React.FC = () => {
  const {t} = useTranslation()
  const ctx = useEntityFormContext()
  const {config, isLoading, error, isSaving, actions} = usePumpSettingsEntity({
    countryId: ctx.countryId,
    businessLine: ctx.businessLine,
    scopeId: ctx.selectedScopeId,
    pendingScope: ctx.pendingScope,
    rootScopeId: ctx.rootScopeId,
  })

  useRegisterEntityForm(actions, isSaving)

  const leadTimeUomOptions = useMemo(
    () =>
      (ctx.pumpSettingsOptions?.availableLeadTimeUnitsOfMeasure ?? [])
        .map((r) => ({value: r.id, label: r.name || r.displaySymbol}))
        .sort((a, b) => {
          const ai = UOM_ORDER.indexOf(a.label)
          const bi = UOM_ORDER.indexOf(b.label)
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
        }),
    [ctx.pumpSettingsOptions],
  )

  const sizeUomOptions = useMemo(
    () =>
      (ctx.pumpSettingsOptions?.availableSizeUnitsOfMeasure ?? []).map((r) => ({
        value: r.id,
        label: r.name || r.displaySymbol,
        symbol: r.displaySymbol || r.name,
      })),
    [ctx.pumpSettingsOptions],
  )

  const hoseUomOptions = useMemo(
    () =>
      (ctx.pumpSettingsOptions?.availableHoseUnitsOfMeasure ?? []).map((r) => ({
        value: r.id,
        label: r.name || r.displaySymbol,
        symbol: r.displaySymbol || r.name,
      })),
    [ctx.pumpSettingsOptions],
  )

  const referenceEventOptions = useMemo(
    () =>
      (ctx.pumpSettingsOptions?.availableReferenceEvents ?? []).map((evt) => ({
        value: evt,
        label: t(`countryConfigurations.pumpSettings.referenceEvents.${evt}`),
      })),
    [ctx.pumpSettingsOptions, t],
  )

  if (isLoading) return <EntityContentSkeleton />
  if (error) return <EntityContentError />
  if (!config) return <EntityContentEmpty />

  return (
    <PumpSettingsForm
      key={ctx.formResetKey}
      config={config}
      isReadOnly={ctx.isReadOnly}
      onDirtyChange={ctx.onDirtyChange}
      onValidChange={ctx.onValidChange}
      onValuesChange={actions.handleValuesChange}
      leadTimeUomOptions={leadTimeUomOptions}
      sizeUomOptions={sizeUomOptions}
      hoseUomOptions={hoseUomOptions}
      referenceEventOptions={referenceEventOptions}
      isRoot={ctx.isCountryDefault}
    />
  )
}
